/**
 * Orbital Cannon — immediate orbital strike
 *
 * Fires during item use. Stone Skin blocks the strike; otherwise the target is killed.
 *
 * Legacy pendingOrbitalCannon state is still read by complex-tick for old saves, but new casts do
 * not create delayed charges.
 *
 * LOS check (RN2): simple presence — target exists in state.players AND is not dead.
 * (state.boardNodeIds membership and graph-reachability deferred to Plan 6 server validation.)
 *
 * Self-registers via addItem(orbitalCannon) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import { appendEvent } from '../event-log';
import { applyDamage } from '../damage';
import { addItem } from './registry';
import { tryBlockWithStoneSkin } from '../stone-skin-block';

export const orbitalCannon: Item = {
  id: 'orbital_cannon',
  name: 'Orbital Cannon',
  description:
    'Charge a satellite strike on a target player. Fires at the start of the next round. ' +
    'Instantly kills the target. Blocked by Stone Skin.',
  targetType: 'player',
  category: 'armory',
  rarity: 'rare',
  blockedByStoneSkin: true,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId } = ctx;
    const targetId = ctx.target.targetPlayerId;

    // Step 1: Validate
    if (!targetId) throw new Error('Orbital Cannon: missing targetPlayerId in ctx.target');
    const target = state.players.get(targetId);
    if (!target) throw new Error(`Orbital Cannon: target not found: ${targetId}`);

    // Step 2: Dead target fails
    if (target.isDead) return { success: false };

    // Step 3: New casts resolve immediately. Clear any legacy delayed charge so it cannot double-fire.
    state.pendingOrbitalCannon = null;

    // Step 4: Emit cast, then beam, then the gameplay result. This lets the client play the
    // cinematic before queued death/respawn state is released.
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'orbital_cannon',
      targetId,
    });

    const blockedByStoneSkin = targetId !== casterId && target.statusEffects.stoneSkin.active;
    const blockedByGodMode = state.godModeImmunity?.get(targetId)?.noDamage === true;
    appendEvent(state, {
      type: 'item_beam_fired',
      playerId: casterId,
      casterId,
      itemId: 'orbital_cannon',
      targetId,
      killed: !blockedByStoneSkin && !blockedByGodMode,
    });

    if (
      tryBlockWithStoneSkin(state, targetId, {
        actorId: casterId,
        sourceId: 'orbital_cannon',
      })
    ) {
      return { success: true, endsTurn: false, blockedByStoneSkin: true };
    }

    applyDamage(state, targetId, 999, 'item_orbital_cannon', casterId);
    return { success: true, endsTurn: false };
  },
};

// Self-register at module load
addItem(orbitalCannon);
